<template>
  <div class="cookie-policy-page">
    <!-- Contenido principal -->
    <div class="main-content">
      <div class="content-card">
        <!-- Introducción -->
        <section class="policy-section">
          <h2 class="section-title">
            {{ t("cookies.politica.intro.titulo") }}
          </h2>
          <p class="section-text">
            {{ t("cookies.politica.intro.texto1") }}
          </p>
          <p class="section-text">
            {{ t("cookies.politica.intro.texto2") }}
          </p>
        </section>

        <div class="section-divider" />

        <!-- Normativa aplicable -->
        <section class="policy-section">
          <h2 class="section-title">
            {{ t("cookies.politica.normativa.titulo") }}
          </h2>
          <ul class="legal-list">
            <li>
              <v-icon
                size="small"
                color="primary"
                class="mr-2"
              >
                mdi-gavel
              </v-icon>
              <span v-html="t('cookies.politica.normativa.lssi')" />
            </li>
            <li>
              <v-icon
                size="small"
                color="primary"
                class="mr-2"
              >
                mdi-gavel
              </v-icon>
              <span v-html="t('cookies.politica.normativa.rgpd')" />
            </li>
            <li>
              <v-icon
                size="small"
                color="primary"
                class="mr-2"
              >
                mdi-gavel
              </v-icon>
              <span v-html="t('cookies.politica.normativa.lopdgdd')" />
            </li>
          </ul>
        </section>

        <div class="section-divider" />

        <!-- ¿Qué son las cookies? -->
        <section class="policy-section">
          <h2 class="section-title">
            {{ t("cookies.politica.queSon.titulo") }}
          </h2>
          <p class="section-text">
            {{ t("cookies.politica.queSon.texto") }}
          </p>
        </section>

        <div class="section-divider" />

        <!-- Cookies utilizadas -->
        <section class="policy-section">
          <h2 class="section-title">
            {{ t("cookies.politica.utilizadas.titulo") }}
          </h2>
          <p class="section-text mb-4">
            {{ t("cookies.politica.utilizadas.intro") }}
          </p>

          <!-- Categoría: Cookies de Análisis -->
          <div class="cookie-category-card">
            <div class="category-header-card">
              <v-icon
                color="primary"
                class="mr-2"
              >
                mdi-chart-line
              </v-icon>
              <h3 class="category-card-title">
                {{ t("cookies.politica.utilizadas.analisis.titulo") }}
              </h3>
            </div>
            <p class="category-card-description">
              {{ t("cookies.politica.utilizadas.analisis.descripcion") }}
            </p>

            <!-- Tabla de cookies -->
            <div class="table-wrapper">
              <v-table class="cookie-table">
                <thead>
                  <tr>
                    <th scope="col">
                      {{ t("cookies.politica.tabla.nombre") }}
                    </th>
                    <th scope="col">
                      {{ t("cookies.politica.tabla.proveedor") }}
                    </th>
                    <th scope="col">
                      {{ t("cookies.politica.tabla.finalidad") }}
                    </th>
                    <th scope="col">
                      {{ t("cookies.politica.tabla.duracion") }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>_ga</code></td>
                    <td>Google Analytics</td>
                    <td>{{ t("cookies.politica.tabla.finalidad_ga") }}</td>
                    <td>
                      {{ t("cookies.politica.tabla.duracion_2anios") }}
                    </td>
                  </tr>
                  <tr>
                    <td><code>_ga_*</code></td>
                    <td>Google Analytics</td>
                    <td>
                      {{ t("cookies.politica.tabla.finalidad_ga_stream") }}
                    </td>
                    <td>
                      {{ t("cookies.politica.tabla.duracion_2anios") }}
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </div>

            <v-alert
              type="info"
              variant="tonal"
              density="compact"
              class="mt-4"
            >
              <strong>{{
                t("cookies.politica.utilizadas.analisis.aviso.titulo")
              }}:</strong>
              {{ t("cookies.politica.utilizadas.analisis.aviso.texto") }}
            </v-alert>
          </div>
        </section>

        <div class="section-divider" />

        <!-- Base legal -->
        <section class="policy-section">
          <h2 class="section-title">
            {{ t("cookies.politica.baseLegal.titulo") }}
          </h2>
          <p class="section-text">
            {{ t("cookies.politica.baseLegal.texto") }}
          </p>
        </section>

        <div class="section-divider" />

        <!-- Cómo gestionar cookies -->
        <section class="policy-section">
          <h2 class="section-title">
            {{ t("cookies.politica.gestionar.titulo") }}
          </h2>
          <p class="section-text mb-4">
            {{ t("cookies.politica.gestionar.texto") }}
          </p>

          <!-- Botón para abrir configuración -->
          <v-btn
            color="primary"
            variant="outlined"
            size="large"
            prepend-icon="mdi-cog"
            class="mb-6"
            @click="openCookieConfig"
          >
            {{ t("cookies.politica.gestionar.boton") }}
          </v-btn>

          <!-- Instrucciones por navegador -->
          <h3 class="subsection-title mb-3">
            {{ t("cookies.politica.gestionar.navegadores.titulo") }}
          </h3>
          <nav
            class="browser-links"
            :aria-label="t('cookies.politica.gestionar.navegadores.ariaEnlaces')"
          >
            <v-chip
              v-for="browser in browsers"
              :key="browser.name"
              :href="browser.url"
              target="_blank"
              rel="noopener noreferrer"
              class="ma-1"
              :prepend-icon="browser.icon"
              color="primary"
              variant="outlined"
              label
            >
              {{ browser.name }}
              <v-icon
                size="x-small"
                class="ml-1"
              >
                mdi-open-in-new
              </v-icon>
            </v-chip>
          </nav>

          <v-alert
            type="warning"
            variant="tonal"
            density="compact"
            class="mt-4"
          >
            <strong>{{
              t("cookies.politica.gestionar.advertencia.titulo")
            }}:</strong>
            {{ t("cookies.politica.gestionar.advertencia.texto") }}
          </v-alert>
        </section>

        <div class="section-divider" />

        <!-- Política de privacidad de terceros -->
        <section class="policy-section">
          <h2 class="section-title">
            {{ t("cookies.politica.terceros.titulo") }}
          </h2>
          <p class="section-text mb-4">
            {{ t("cookies.politica.terceros.texto") }}
          </p>

          <v-card
            variant="outlined"
            class="third-party-card"
          >
            <v-card-item>
              <div class="d-flex align-center">
                <v-avatar
                  color="primary"
                  class="mr-3"
                >
                  <v-icon color="white">
                    mdi-google
                  </v-icon>
                </v-avatar>
                <div class="flex-grow-1">
                  <h4 class="text-h6 mb-1">
                    Google Analytics
                  </h4>
                  <p class="text-body-2 text-medium-emphasis mb-0">
                    {{ t("cookies.politica.terceros.google.descripcion") }}
                  </p>
                </div>
              </div>
            </v-card-item>
            <v-card-actions>
              <v-btn
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                variant="text"
                append-icon="mdi-open-in-new"
              >
                {{ t("cookies.politica.terceros.google.verPolitica") }}
              </v-btn>
              <v-btn
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                variant="text"
                append-icon="mdi-open-in-new"
              >
                {{ t("cookies.politica.terceros.google.desactivar") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </section>

        <div class="section-divider" />

        <!-- Actualización de la política -->
        <section class="policy-section">
          <h2 class="section-title">
            {{ t("cookies.politica.actualizacion.titulo") }}
          </h2>
          <p class="section-text">
            {{ t("cookies.politica.actualizacion.texto") }}
          </p>
        </section>

        <div class="section-divider" />

        <!-- Contacto -->
        <section class="policy-section last">
          <h2 class="section-title">
            {{ t("cookies.politica.contacto.titulo") }}
          </h2>
          <p class="section-text">
            {{ t("cookies.politica.contacto.texto") }}
          </p>
          <div class="contact-box">
            <v-icon color="primary">
              mdi-email
            </v-icon>
            <div>
              <p class="contact-label">
                {{ t("cookies.politica.contacto.etiqueta") }}
              </p>
              <a
                href="mailto:calidad.telecos.soporte@cnmc.es"
                class="contact-email"
              >
                calidad.telecos.soporte@cnmc.es
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from "vue-i18n";
import { useCookieConsent } from "@/composables/useCookieConsent";

const { t, locale } = useI18n();
const { showConfigModal } = useCookieConsent();

// URLs de ayuda de navegadores según el idioma
const browserUrls = {
  es: {
    chrome: "https://support.google.com/chrome/answer/95647?hl=es",
    firefox:
      "https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias",
    safari: "https://support.apple.com/es-es/guide/safari/sfri11471/mac",
    edge: "https://support.microsoft.com/es-es/microsoft-edge/eliminar-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09",
    opera: "https://help.opera.com/en/latest/web-preferences/#cookies",
  },
  en: {
    chrome: "https://support.google.com/chrome/answer/95647?hl=en",
    firefox:
      "https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop",
    safari: "https://support.apple.com/en-us/guide/safari/sfri11471/mac",
    edge: "https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09",
    opera: "https://help.opera.com/en/latest/web-preferences/#cookies",
  },
};

const getBrowserUrl = (browser) => {
  const lang = locale.value === "en" ? "en" : "es";
  return browserUrls[lang][browser] || browserUrls.es[browser];
};

const browsers = [
  {
    name: "Chrome",
    icon: "mdi-google-chrome",
    get url() {
      return getBrowserUrl("chrome");
    },
  },
  {
    name: "Firefox",
    icon: "mdi-firefox",
    get url() {
      return getBrowserUrl("firefox");
    },
  },
  {
    name: "Safari",
    icon: "mdi-apple-safari",
    get url() {
      return getBrowserUrl("safari");
    },
  },
  {
    name: "Edge",
    icon: "mdi-microsoft-edge",
    get url() {
      return getBrowserUrl("edge");
    },
  },
  {
    name: "Opera",
    icon: "mdi-opera",
    get url() {
      return getBrowserUrl("opera");
    },
  },
];

const openCookieConfig = () => {
  showConfigModal.value = true;
};
</script>

<style scoped>
.cookie-policy-page {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
}

/* ===== Main Content ===== */
.main-content {
  padding: 16px 0 24px;
}

.content-card {
  background: linear-gradient(135deg, #f8fafc 0%, #fff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 28px;
}

/* ===== Policy Sections ===== */
.policy-section {
  margin-bottom: 28px;
}

.policy-section.last {
  margin-bottom: 0;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 14px;
}

.subsection-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #374151;
}

.section-text {
  font-size: 0.875rem;
  line-height: 1.7;
  color: #475569;
  margin-bottom: 14px;
}

/* ===== Divider ===== */
.section-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, #e5e7eb, transparent);
  margin: 24px 0;
}

/* ===== Legal List ===== */
.legal-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.legal-list li {
  display: flex;
  align-items: flex-start;
  padding: 10px 0;
  font-size: 0.8125rem;
  line-height: 1.6;
  color: #475569;
}

.legal-list li .v-icon {
  opacity: 0.8;
}

/* ===== Cookie Category Card ===== */
.cookie-category-card {
  background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #f0f0f0;
  margin-bottom: 20px;
}

.category-header-card {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.category-header-card .v-icon {
  opacity: 0.8;
}

.category-card-title {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.category-card-description {
  font-size: 0.8125rem;
  line-height: 1.6;
  color: #475569;
  margin-bottom: 16px;
}

/* ===== Cookie Table ===== */
.table-wrapper {
  overflow-x: auto;
}

.cookie-table {
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 14px;
  min-width: 500px;
  border: 1px solid #f0f0f0;
}

.cookie-table th {
  background: linear-gradient(135deg, #f5f7f9 0%, #f0f2f4 100%);
  font-weight: 600;
  font-size: 0.75rem;
  color: #374151;
  padding: 10px 14px !important;
}

.cookie-table td {
  padding: 10px 14px !important;
  color: #475569;
  font-size: 0.8125rem;
}

.cookie-table code {
  background: #fff;
  border: 1px solid #e5e7eb;
  color: #374151;
  padding: 3px 6px;
  border-radius: 4px;
  font-family: "Roboto Mono", monospace;
  font-size: 0.75rem;
}

/* ===== Browser Links ===== */
.browser-links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* ===== Third Party Card ===== */
.third-party-card {
  border-color: #e5e7eb;
}

/* ===== Contact Box ===== */
.contact-box {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
  border-radius: 10px;
  border: 1px solid #f0f0f0;
}

.contact-box .v-icon {
  opacity: 0.8;
}

.contact-label {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0 0 2px 0;
}

.contact-email {
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.875rem;
  transition: color 0.15s ease;
}

.contact-email:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.contact-email:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
  border-radius: 2px;
}

/* ===== Responsive ===== */
@media (max-width: 768px) {

  .main-content {
    padding: 16px;
  }

  .content-card {
    padding: 20px;
  }
}

@media (max-width: 600px) {
  .section-title {
    font-size: 0.9375rem;
  }

  .section-text {
    font-size: 0.8125rem;
  }

  .cookie-category-card {
    padding: 16px;
  }

  .cookie-table {
    font-size: 0.75rem;
  }

  .cookie-table th,
  .cookie-table td {
    padding: 8px !important;
  }

  .content-card {
    padding: 16px;
  }
}
</style>
